"use client"

import { useCallback, useMemo } from "react"
import { useTenantSettings } from "./use-tenant"
import type { SavedEconomicsPreset, AnalysisItem } from "@/components/shopify/economics/types"
import { useTranslations } from "next-intl"


const DEFAULT_NAMESPACE = "shopify_economics_presets"
const MAX_PRESETS_PER_INTEGRATION = 20

interface PresetsConfig {
  presets: SavedEconomicsPreset[]
}

export function useEconomicsPresets(integrationId: string, namespace = DEFAULT_NAMESPACE) {
  const tUi = useTranslations("Feedback.SettingsUseEconomicsPresets")

  const { settings, loading, error, refetch, createSetting, updateSetting } =
    useTenantSettings(namespace)

  const settingRow = useMemo(
    () => settings.find((s) => s.namespace === namespace),
    [settings, namespace],
  )

  // All presets for this integration, ordered by updated_at desc
  const presets = useMemo(() => {
    const all = (settingRow?.config as PresetsConfig)?.presets ?? []
    return all
      .filter((p) => p.integration_id === integrationId)
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
  }, [settingRow, integrationId])

  const persistPresets = useCallback(
    async (next: SavedEconomicsPreset[]) => {
      const config: PresetsConfig = { presets: next }
      if (settingRow) {
        return updateSetting(settingRow.id, { config })
      }
      return createSetting({
        namespace,
        name: namespace,
        config,
        enabled: true,
        description: tUi("savedEconomicsAnalysisPresets"),
      })
    },
    [settingRow, createSetting, updateSetting, namespace],
  )

  const getAllPresets = useCallback((): SavedEconomicsPreset[] => {
    return (settingRow?.config as PresetsConfig)?.presets ?? []
  }, [settingRow])

  const savePreset = useCallback(
    async (
      name: string,
      items: AnalysisItem[],
      extra?: Partial<SavedEconomicsPreset>,
    ): Promise<SavedEconomicsPreset> => {
      const all = getAllPresets()
      const forIntegration = all.filter((p) => p.integration_id === integrationId)
      if (forIntegration.length >= MAX_PRESETS_PER_INTEGRATION) {
        throw new Error(`Maximum ${MAX_PRESETS_PER_INTEGRATION} presets per integration`)
      }

      const now = new Date().toISOString()
      const preset: SavedEconomicsPreset = {
        id: crypto.randomUUID(),
        integration_id: integrationId,
        name,
        items,
        ...extra,
        created_at: now,
        updated_at: now,
      }

      await persistPresets([...all, preset])
      return preset
    },
    [integrationId, getAllPresets, persistPresets],
  )

  const updatePreset = useCallback(
    async (
      id: string,
      updates: Partial<Pick<SavedEconomicsPreset, "name" | "items" | "baseline_mode" | "custom_baseline_start" | "custom_baseline_end">>,
    ) => {
      const all = getAllPresets()
      const idx = all.findIndex((p) => p.id === id)
      if (idx === -1) throw new Error(tUi("presetNotFound"))
      const updated = {
        ...all[idx],
        ...updates,
        updated_at: new Date().toISOString(),
      }
      const next = [...all]
      next[idx] = updated
      await persistPresets(next)
    },
    [getAllPresets, persistPresets, tUi],
  )

  const deletePreset = useCallback(
    async (id: string) => {
      const all = getAllPresets()
      await persistPresets(all.filter((p) => p.id !== id))
    },
    [getAllPresets, persistPresets],
  )

  return { presets, loading, error, refetch, savePreset, updatePreset, deletePreset }
}
